import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  parseEnumFromString,
  parseStringFromEnum,
  TAB_QUERY_STRING,
} from '../../../utils/url-tabs-helper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TabPanel, { a11yProps } from '../../misc/TabsPanel';
import GeneralRobotSettings from './GeneralRobotSettings';
import SystemInformation from './SystemInformation';
import TorizonUpdateCard from './TorizonUpdateCard';
import RobotAccessSettings from './RobotAccessSettings';
import HistoryPage from './HistoryPage';
import Divider from '@mui/material/Divider';
import SphereLogo from '../../../media/logo1024.png';
import { RobotIcon } from '@common/utils/icons/icons';
import CleaningConfigurationPage from '@common/components/Configuration/CleaningConfigurationPage';
import ButtonConfiguration from '@common/components/Configuration/ButtonConfiguration/ButtonConfigurationPage';
import Collapse from '@mui/material/Collapse';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectWebappHasUnsyncedChanges } from '@common/store/app-configurations/app-configurations-selectors';
import Container from '@mui/material/Container';
import ExcludedDates from '@common/components/Configuration/ExcludedDates/ExcludedDates';
import { useAppSelector } from 'store';
import {
  selectHasCompletedFullDataSync,
  selectIsRobotDeploymentActive,
} from 'store/robots/robots-slice-selectors';
import ForceSyncButton from '@common/components/Websockets/ForceSyncButton';
import SupportInformation from './support/SupportInformation';

enum PageTabs {
  General,
  History,
  RobotAccess,
  System,
  Support,

  CleaningSettings,
  ButtonConfiguration,
  Dates,
}

const RobotSettingsPage: React.FC<{ webSoftwareVersion?: string | null }> = ({
  webSoftwareVersion,
}) => {
  const { t } = useTranslation();

  const [filterParams, setFilterParams] = useSearchParams();
  const pageTab = parseEnumFromString(PageTabs, filterParams.get(TAB_QUERY_STRING));

  const webappHasUnsyncedChanges = useSharedSelector((state) =>
    selectWebappHasUnsyncedChanges(state),
  );
  const isRobotActive = useAppSelector((state) => selectIsRobotDeploymentActive(state));
  const hasCompletedFullDataSync = useAppSelector((state) => selectHasCompletedFullDataSync(state));

  const [tabsValue, setTabsValue] = useState(pageTab);

  const tabChangeHandler = useCallback(
    (tab: PageTabs) => {
      setFilterParams((prevState) => {
        prevState.set(TAB_QUERY_STRING, parseStringFromEnum(PageTabs, tab));
        return prevState;
      });
      setTabsValue(tab);
    },
    [setFilterParams],
  );

  useEffect(() => {
    if (tabsValue !== pageTab) {
      tabChangeHandler(pageTab);
    }
  }, [pageTab, tabChangeHandler, tabsValue]);

  const tabChangeClickHandler = (_event: React.SyntheticEvent, newValue: number) => {
    tabChangeHandler(newValue);
  };

  // Required to effectively disable the tabs checking for values outside its tab index range
  const cloudConfigTabValue = tabsValue > PageTabs.Support ? false : tabsValue;
  const robotConfigTabValue = tabsValue > PageTabs.Support ? tabsValue : false;

  return (
    <React.Fragment>
      <Stack spacing={2} sx={{ px: { xs: 1, sm: 0 } }}>
        <Stack
          spacing={2}
          direction="row"
          sx={{ width: '100%', overflowX: 'auto', scrollbarWidth: 'none' }}
        >
          <Stack direction="row" alignItems="center" flexShrink={0}>
            <img
              src={SphereLogo}
              alt=""
              style={{
                height: '24px',
                width: '24px',
                marginRight: '16px',
              }}
            />
            <Tabs value={cloudConfigTabValue} onChange={tabChangeClickHandler}>
              <Tab label={t('title-general-settings')} {...a11yProps(PageTabs.General)} />
              <Tab label={t('title-history-settings')} {...a11yProps(PageTabs.History)} />
              <Tab label={t('title-robot-access-settings')} {...a11yProps(PageTabs.RobotAccess)} />
              <Tab label={t('title-system-settings')} {...a11yProps(PageTabs.System)} />
              <Tab label={t('title-robot-support')} {...a11yProps(PageTabs.Support)} />
            </Tabs>
          </Stack>
          {isRobotActive && hasCompletedFullDataSync && (
            <React.Fragment>
              <Divider flexItem orientation="vertical" />
              <Stack direction="row" alignItems="center" flexShrink={0}>
                <RobotIcon color="info" sx={{ mx: 2 }} />
                <Tabs value={robotConfigTabValue} onChange={tabChangeClickHandler}>
                  <Tab
                    label={t('configuration-cleaning')}
                    {...a11yProps(PageTabs.CleaningSettings)}
                  />
                  <Tab
                    label={t('configuration-buttons')}
                    {...a11yProps(PageTabs.ButtonConfiguration)}
                  />
                  <Tab label={t('configuration-dates')} {...a11yProps(PageTabs.Dates)} />
                </Tabs>
              </Stack>
            </React.Fragment>
          )}
        </Stack>

        <Stack>
          <Container maxWidth="lg" sx={{ p: 0 }}>
            <Collapse in={webappHasUnsyncedChanges}>
              <CustomAlert
                sx={{ mb: 2 }}
                severity="info"
                header={t('info-webapp-has-unsynced-changes')}
                action={<ForceSyncButton />}
              />
            </Collapse>
          </Container>

          <TabPanel value={tabsValue} index={PageTabs.General}>
            <GeneralRobotSettings />
          </TabPanel>
          <TabPanel value={tabsValue} index={PageTabs.History}>
            <HistoryPage />
          </TabPanel>
          <TabPanel value={tabsValue} index={PageTabs.RobotAccess}>
            <RobotAccessSettings />
          </TabPanel>
          <TabPanel value={tabsValue} index={PageTabs.System}>
            <Stack width="100%" alignItems="center" p={0.3} spacing={2}>
              <TorizonUpdateCard />
              <SystemInformation />
            </Stack>
          </TabPanel>
          <TabPanel value={tabsValue} index={PageTabs.Support}>
            <SupportInformation />
          </TabPanel>
          {isRobotActive && hasCompletedFullDataSync && (
            <React.Fragment>
              <TabPanel value={tabsValue} index={PageTabs.CleaningSettings}>
                <CleaningConfigurationPage webSoftwareVersion={webSoftwareVersion} />
              </TabPanel>
              <TabPanel value={tabsValue} index={PageTabs.ButtonConfiguration}>
                <ButtonConfiguration />
              </TabPanel>
              <TabPanel value={tabsValue} index={PageTabs.Dates}>
                <ExcludedDates />
              </TabPanel>
            </React.Fragment>
          )}
        </Stack>
      </Stack>
    </React.Fragment>
  );
};

export default RobotSettingsPage;
